import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import {
  FaRobot,
  FaPaperPlane,
  FaShieldAlt,
  FaUtensils,
  FaUsers,
  FaExclamationTriangle,
  FaLightbulb,
  FaClock,
  FaCheckCircle,
  FaSpinner,
} from "react-icons/fa";

const HostelAIAssistant = () => {
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "👋 Hello Warden! I am your AI Hostel Copilot. I analyze real-time room occupancy, curfew risks, mess dining forecasts, and maintenance SLA logs. How can I assist you today?",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [fetchingInsights, setFetchingInsights] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchAIInsights();
  }, []);

  const fetchAIInsights = async () => {
    try {
      setFetchingInsights(true);
      const res = await axios.get(`${baseApiURL()}/hostel/ai-insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsights(res.data?.insights);
    } catch (err) {
      console.error("AI Insights Fetch Error:", err);
    } finally {
      setFetchingInsights(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || loading) return;

    const userText = prompt.trim();
    setPrompt("");
    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setLoading(true);

    try {
      const res = await axios.post(
        `${baseApiURL()}/hostel/ai-copilot`,
        { prompt: userText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        setMessages((prev) => [...prev, { role: "ai", text: res.data.answer }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "ai", text: "⚠️ Could not retrieve AI analysis. Please try again." },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: "⚠️ Connection error to AI engine. Please verify network or API keys." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quickPrompts = [
    "What is the current available bed capacity?",
    "Are there any students overdue past curfew?",
    "What is the mess headcount forecast for dinner?",
    "List critical pending maintenance tickets",
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 p-6 rounded-2xl text-white shadow-lg flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-white/20 rounded-xl backdrop-blur-xs">
              <FaRobot className="text-xl" />
            </span>
            <span className="text-xs font-bold uppercase tracking-widest text-violet-200">
              Machine Learning & NLP Engine
            </span>
          </div>
          <h2 className="text-xl md:text-2xl font-black mt-2">
            Hostel Intelligence & Predictive Copilot
          </h2>
          <p className="text-xs text-violet-100 mt-1 max-w-xl">
            Live behavioral pattern analytics, curfew risk anomaly detection, mess wastage minimization, and roommate synergy optimizer.
          </p>
        </div>

        <button
          onClick={fetchAIInsights}
          disabled={fetchingInsights}
          className="px-4 py-2.5 rounded-xl text-xs font-bold bg-white text-violet-700 hover:bg-violet-50 cursor-pointer shadow-md transition flex items-center gap-2"
        >
          {fetchingInsights ? <FaSpinner className="animate-spin" /> : "⚡ Refresh AI Models"}
        </button>
      </div>

      {/* AI Insights Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Curfew & Absenteeism Risk */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
              <FaShieldAlt /> Curfew Anomaly Risk
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300">
              {insights?.curfewRisk?.overdueCount || 0} Overdue
            </span>
          </div>

          <div className="text-xs text-slate-600 dark:text-slate-300 space-y-2">
            {insights?.curfewRisk?.overdueList?.length > 0 ? (
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {insights.curfewRisk.overdueList.map((st, i) => (
                  <div key={i} className="p-2 rounded-lg bg-rose-50 dark:bg-rose-950/30 text-[11px]">
                    <span className="font-bold text-rose-700 dark:text-rose-300">{st.studentName}</span> ({st.enrollmentNo})
                    <p className="text-slate-500">Destination: {st.destination}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                <FaCheckCircle /> All out-students currently within valid curfew boundaries.
              </div>
            )}
          </div>
        </div>

        {/* Mess Wastage Minimizer */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <FaUtensils /> Mess Headcount Forecaster
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              {insights?.messForecast?.estimatedWastageReductionKg || "0 kg"}
            </span>
          </div>

          <div className="space-y-2 text-xs">
            <div className="flex justify-between text-slate-700 dark:text-slate-300">
              <span>Expected Diners Tonight:</span>
              <span className="font-black text-amber-600 text-sm">{insights?.messForecast?.expectedDinersTonight || 0}</span>
            </div>
            <div className="flex justify-between text-slate-500 text-[11px]">
              <span>Active Approved Leaves:</span>
              <span>{insights?.messForecast?.studentsOnLeave || 0} Hostellers</span>
            </div>
            <p className="text-[11px] text-slate-500 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg">
              💡 {insights?.messForecast?.aiRecommendation}
            </p>
          </div>
        </div>

        {/* Maintenance SLA Bottlenecks */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-3">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 text-purple-600 dark:text-purple-400">
              <FaExclamationTriangle /> Maintenance SLA Monitor
            </span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300">
              {insights?.maintenanceSLA?.totalOpen || 0} Open
            </span>
          </div>

          <div className="space-y-2 text-xs">
            <p className="text-[11px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg">
              {insights?.maintenanceSLA?.aiRecommendation}
            </p>
            <div className="flex justify-between text-[11px] text-slate-500">
              <span>Critical Priority Tickets:</span>
              <span className="font-bold text-rose-500">{insights?.maintenanceSLA?.criticalPending || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Roommate Synergy Insights */}
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <FaUsers className="text-indigo-500" /> AI Roommate Academic Compatibility & Synergy
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {insights?.roommateSynergy?.map((syn, idx) => (
            <div key={idx} className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="font-bold text-slate-900 dark:text-slate-100">Room {syn.roomNumber}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                  {syn.synergyScore}
                </span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">
                {syn.students.join(", ")}
              </div>
              <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium">
                {syn.recommendation}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Live AI Copilot Chat Interface */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
          <FaRobot className="text-violet-600 text-lg" />
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Hostel Warden Copilot Chat
            </h3>
            <p className="text-[11px] text-slate-500">
              Ask any question about students, rooms, fees, gate passes, or dining
            </p>
          </div>
        </div>

        {/* Quick Suggestion Pills */}
        <div className="flex flex-wrap gap-2">
          {quickPrompts.map((qp, idx) => (
            <button
              key={idx}
              onClick={() => setPrompt(qp)}
              className="text-[11px] font-medium px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-violet-100 dark:hover:bg-violet-950/50 hover:text-violet-700 transition cursor-pointer"
            >
              💬 {qp}
            </button>
          ))}
        </div>

        {/* Message Stream */}
        <div className="space-y-3 max-h-80 overflow-y-auto p-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800/60">
          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-xl p-3.5 rounded-2xl text-xs leading-relaxed ${
                  m.role === "user"
                    ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-br-none shadow-xs"
                    : "bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-800 rounded-bl-none shadow-xs"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-xs flex items-center gap-2 text-slate-500">
                <FaSpinner className="animate-spin text-violet-600" />
                <span>AI analyzing live hostel data...</span>
              </div>
            </div>
          )}
        </div>

        {/* Input Form */}
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            placeholder="Ask AI Copilot (e.g. How many students are late today?)..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs focus:outline-none focus:ring-2 focus:ring-violet-500 text-slate-900 dark:text-slate-100"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-5 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-violet-600 to-purple-600 text-white hover:opacity-90 cursor-pointer shadow-md transition flex items-center gap-2"
          >
            <FaPaperPlane /> Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default HostelAIAssistant;
